import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useNavigate } from "react-router-dom";
import {
  Barbell, Heartbeat, PersonSimpleRun, Scan, MapPin, EnvelopeSimple, Phone,
  Clock, ArrowRight, Check, InstagramLogo, List, X,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { BrandLogo } from "@/components/BrandLogo";

const INTERIOR_IMG = "/images/oto-interior.jpg";
const EXTERIOR_IMG = "/images/oto-exterior.jpg";
const PILATES_IMG = "https://images.unsplash.com/photo-1747239069226-55382c570116?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const PT_IMG = "/images/erin.jpg";
const SCAN_IMG = "/images/styku-scan.jpeg";

const GM_SIGNUP = "https://otorohangahf.gymmasteronline.com/portal/signup";
const GM_LOGIN = "https://otorohangahf.gymmasteronline.com/portal/login";
const openGM = (u) => window.open(u, "_blank", "noopener,noreferrer");

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Landing() {
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [menuOpen, setMenuOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [content, setContent] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const openAuth = (mode) => {
    setAuthMode(mode);
    setAuthOpen(true);
  };

  const goDash = () => navigate("/dashboard");

  const nav = [
    { label: "Services", href: "#services" },
    { label: "Classes", to: "/classes" },
    { label: "Memberships", to: "/memberships" },
    { label: "Contact", href: "#contact" },
  ];

  const navClick = (n) => {
    if (n.to) navigate(n.to);
  };

  return (
    <div className="bg-white text-zinc-950 overflow-x-hidden">
      {/* NAV */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-zinc-200/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <BrandLogo onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} />
          <nav className="hidden md:flex items-center gap-10">
            {nav.map((n) =>
              n.to ? (
                <button key={n.label} onClick={() => navClick(n)} className="text-sm font-bold hover:text-[#FF4F00] transition-colors" data-testid={`nav-${n.label.toLowerCase()}`}>
                  {n.label}
                </button>
              ) : (
                <a key={n.label} href={n.href} className="text-sm font-bold hover:text-[#FF4F00] transition-colors">
                  {n.label}
                </a>
              )
            )}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <button onClick={goDash} data-testid="nav-dashboard-btn" className="bg-zinc-950 text-white rounded-sm px-5 py-2.5 text-sm font-bold hover:bg-zinc-800 transition-colors">
                My Account
              </button>
            ) : (
              <>
                <button onClick={() => openAuth("login")} data-testid="nav-login-btn" className="text-sm font-bold hover:text-[#FF4F00] transition-colors">
                  Sign in
                </button>
                <button onClick={() => openGM(GM_LOGIN)} data-testid="nav-join-btn" className="bg-[#FF4F00] text-white rounded-sm px-5 py-2.5 text-sm font-bold hover:bg-[#E04600] transition-colors">
                  Join Now
                </button>
              </>
            )}
          </div>
          <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
            {menuOpen ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-zinc-200 px-6 py-4 space-y-4 bg-white">
            {nav.map((n) =>
              n.to ? (
                <button key={n.label} onClick={() => { setMenuOpen(false); navigate(n.to); }} className="block font-bold text-left w-full">
                  {n.label}
                </button>
              ) : (
                <a key={n.label} href={n.href} onClick={() => setMenuOpen(false)} className="block font-bold">
                  {n.label}
                </a>
              )
            )}
            {user ? (
              <button onClick={goDash} className="w-full bg-zinc-950 text-white rounded-sm py-3 font-bold">My Account</button>
            ) : (
              <div className="flex gap-3">
                <button onClick={() => openAuth("login")} className="flex-1 border-2 border-zinc-200 rounded-sm py-3 font-bold">Sign in</button>
                <button onClick={() => openGM(GM_LOGIN)} className="flex-1 bg-[#FF4F00] text-white rounded-sm py-3 font-bold">Join Now</button>
              </div>
            )}
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="top" className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-20 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div initial="hidden" animate="show" className="lg:col-span-6">
          <motion.div variants={fade} custom={0} className="inline-flex items-center gap-2 border border-zinc-200 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#FF4F00] animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Open 24 Hours · Otorohanga</span>
          </motion.div>
          <motion.h1 variants={fade} custom={1} className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
            {content.hero_title || "Eat Clean. Train Dirty."}
          </motion.h1>
          <motion.p variants={fade} custom={2} className="mt-6 text-lg text-zinc-600 max-w-md leading-relaxed">
            {content.hero_subtitle || "Otorohanga's home of 24/7 fitness, Reformer Pilates & real results."}
          </motion.p>
          <motion.div variants={fade} custom={3} className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => openGM(GM_LOGIN)} data-testid="hero-join-btn" className="inline-flex items-center gap-2 bg-[#FF4F00] text-white rounded-sm px-7 py-4 font-bold tracking-wide hover:bg-[#E04600] transition-colors">
              Join Now <ArrowRight size={18} weight="bold" />
            </button>
            <button onClick={() => navigate("/classes")} data-testid="hero-view-classes-btn" className="inline-flex items-center gap-2 border-2 border-zinc-200 rounded-sm px-7 py-4 font-bold hover:border-zinc-950 transition-colors">
              View Classes
            </button>
            <a href="https://form.jotform.com/Erin_Milne/form" target="_blank" rel="noopener noreferrer" data-testid="hero-book-pt-btn" className="inline-flex items-center gap-2 bg-[#FF4F00] text-white rounded-sm px-7 py-4 font-bold tracking-wide hover:bg-[#E04600] transition-colors">
              <PersonSimpleRun size={18} weight="bold" /> Book PT with Erin
            </a>
          </motion.div>
          <motion.div variants={fade} custom={4} className="mt-10 flex items-center gap-8">
            <div>
              <div className="font-heading text-3xl font-black">24/7</div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">Access</div>
            </div>
            <div className="w-px h-10 bg-zinc-200" />
            <div>
              <div className="font-heading text-3xl font-black">3D</div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">Styku Scans</div>
            </div>
            <div className="w-px h-10 bg-zinc-200" />
            <div>
              <div className="font-heading text-3xl font-black">1:1</div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">PT w/ Erin</div>
            </div>
          </motion.div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="lg:col-span-6 relative">
          <div className="relative rounded-sm overflow-hidden aspect-[4/5]">
            <img src={INTERIOR_IMG} alt="Inside Otorohanga Health & Fitness — training floor" className="w-full h-full object-cover" />
            <div className="absolute bottom-4 left-4 right-4 backdrop-blur-md bg-white/85 rounded-sm p-4 flex items-center gap-3">
              <Clock size={22} weight="bold" className="text-[#FF4F00]" />
              <span className="text-sm font-bold">{content.hours || "Open 24 hours, 7 days a week"}</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="bg-zinc-950 py-5">
        <Marquee speed={40} gradient={false}>
          {["24/7 ACCESS", "REFORMER PILATES", "STYKU 3D SCAN", "PERSONAL TRAINING", "COMMUNITY"].map((t) => (
            <span key={t} className="marquee-item text-white flex items-center">
              {t} <span className="text-[#FF4F00] mx-8">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* SERVICES BENTO */}
      <section id="services" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="mb-12 max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4F00] mb-3">What we offer</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight">Everything you need to move better.</h2>
        </div>
        <div className="grid md:grid-cols-6 gap-5 auto-rows-[minmax(200px,auto)]">
          <ServiceCard className="md:col-span-4 md:row-span-2" icon={Barbell} title="24-Hour Gym Access" desc="Full strength & cardio floor, open around the clock. Train on your schedule, never on someone else's." image={EXTERIOR_IMG} big />
          <ServiceCard className="md:col-span-2" icon={Heartbeat} title="Reformer Pilates" desc="Small-group reformer classes with Erin." image={PILATES_IMG} />
          <ServiceCard className="md:col-span-2" icon={Scan} title="Styku 3D Body Scan" desc="Measure what matters — track fat loss & muscle gain." image={SCAN_IMG} />
          <ServiceCard className="md:col-span-3" icon={PersonSimpleRun} title="Personal Training with Erin" desc="1-on-1 coaching to build a stronger, healthier you." image={PT_IMG} />
          <div className="md:col-span-3 bg-[#FF4F00] rounded-sm p-8 flex flex-col justify-between text-white hover-lift">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] opacity-80 mb-3">Ready to start?</p>
              <h3 className="font-heading text-2xl font-black leading-tight">Your first step to feeling unstoppable starts today.</h3>
            </div>
            <button onClick={() => openGM(GM_SIGNUP)} data-testid="services-join-btn" className="mt-6 inline-flex items-center gap-2 bg-white text-zinc-950 rounded-sm px-6 py-3 font-bold self-start hover:bg-zinc-100 transition-colors">
              Become a member <ArrowRight size={18} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      {/* MEMBERSHIP */}
      <section id="membership" className="bg-zinc-50 border-y border-zinc-200 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4F00] mb-3">Membership</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight">Simple plans. No lock-in games.</h2>
            <p className="text-zinc-500 mt-3">Ongoing weekly & fortnightly, or pay upfront and save. Full pricing, passes and services on the memberships page.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {plans.filter((p) => p.category === "ongoing").map((p, i) => (
              <motion.div
                key={p.id}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fade}
                custom={i}
                className={`rounded-sm p-8 flex flex-col ${p.recommended ? "bg-zinc-950 text-white" : "bg-white border border-zinc-200"}`}
                data-testid={`plan-card-${i}`}
              >
                {p.recommended && (
                  <span className="self-start bg-[#FF4F00] text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">Most Popular</span>
                )}
                <h3 className="font-heading text-2xl font-black">{p.name}</h3>
                <p className={`text-sm mt-1 ${p.recommended ? "text-zinc-400" : "text-zinc-500"}`}>{p.description}</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="font-heading text-5xl font-black">${p.price}</span>
                  <span className={`mb-1.5 text-sm ${p.recommended ? "text-zinc-400" : "text-zinc-500"}`}>/{p.interval}</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features?.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm">
                      <Check size={18} weight="bold" className="text-[#FF4F00] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => openGM(GM_SIGNUP)}
                  data-testid={`plan-select-${i}`}
                  className={`mt-8 rounded-sm py-3.5 font-bold tracking-wide transition-colors ${p.recommended ? "bg-[#FF4F00] text-white hover:bg-[#E04600]" : "bg-zinc-950 text-white hover:bg-zinc-800"}`}
                >
                  Choose {p.name}
                </button>
              </motion.div>
            ))}
            {/* CTA card → full memberships page */}
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fade}
              custom={2}
              className="rounded-sm p-8 flex flex-col justify-between bg-[#FF4F00] text-white hover-lift"
            >
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] opacity-80 mb-3">Also available</p>
                <h3 className="font-heading text-2xl font-black leading-tight">Full-cost plans, passes & services</h3>
                <ul className="mt-5 space-y-2 text-sm">
                  <li className="flex items-center gap-2"><Check size={16} weight="bold" /> 1 / 3 / 6 / 12 month prepaid</li>
                  <li className="flex items-center gap-2"><Check size={16} weight="bold" /> Day pass & 7-day pass</li>
                  <li className="flex items-center gap-2"><Check size={16} weight="bold" /> Styku scan & PT with Erin</li>
                </ul>
              </div>
              <button onClick={() => navigate("/memberships")} data-testid="view-all-memberships-btn" className="mt-6 inline-flex items-center gap-2 bg-white text-zinc-950 rounded-sm px-6 py-3 font-bold self-start hover:bg-zinc-100 transition-colors">
                View all plans <ArrowRight size={18} weight="bold" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4F00] mb-3">Let's connect</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight mb-6">Come train with us in Otorohanga.</h2>
          <p className="text-zinc-600 leading-relaxed mb-8 max-w-md">{content.about}</p>
          <div className="space-y-5">
            <ContactRow icon={MapPin} label="Location" value={content.address || "40 Te Kanawa Street, Otorohanga 3900"} />
            <ContactRow icon={EnvelopeSimple} label="Email" value={content.email || "Erin@otofitness.co.nz"} href={`mailto:${content.email || "Erin@otofitness.co.nz"}`} />
            <ContactRow icon={Phone} label="Phone" value={content.phone || "021 154 1728"} href={`tel:${content.phone || "0211541728"}`} />
            <ContactRow icon={InstagramLogo} label="Instagram" value="@otohealthfitness" href="https://instagram.com/otohealthfitness" />
          </div>
        </div>
        <div className="rounded-sm overflow-hidden border border-zinc-200 aspect-[4/3]">
          <iframe
            title="map"
            className="w-full h-full"
            loading="lazy"
            src="https://maps.google.com/maps?q=40%20Te%20Kanawa%20Street%20Otorohanga&t=&z=15&ie=UTF8&iwloc=&output=embed"
          />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-zinc-950 text-white py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="font-heading font-black text-lg">
            OTOROHANGA<span className="text-[#FF4F00]">.</span> HEALTH & FITNESS
          </div>
          <p className="text-sm text-zinc-500">© {new Date().getFullYear()} Otorohanga Health & Fitness. All rights reserved.</p>
        </div>
      </footer>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />
    </div>
  );
}

const ServiceCard = ({ className, icon: Icon, title, desc, image, big, imgClassName = "object-cover" }) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true }}
    variants={fade}
    className={`relative rounded-sm overflow-hidden group hover-lift ${className}`}
  >
    <img src={image} alt={title} className={`absolute inset-0 w-full h-full ${imgClassName}`} />
    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-zinc-950/30 to-transparent" />
    <div className="relative h-full flex flex-col justify-end p-6 text-white">
      <div className="w-11 h-11 rounded-sm bg-[#FF4F00] flex items-center justify-center mb-4">
        <Icon size={22} weight="bold" />
      </div>
      <h3 className={`font-heading font-black tracking-tight ${big ? "text-3xl" : "text-xl"}`}>{title}</h3>
      <p className="text-sm text-zinc-200 mt-2 max-w-sm">{desc}</p>
    </div>
  </motion.div>
);

const ContactRow = ({ icon: Icon, label, value, href }) => {
  const Inner = (
    <>
      <div className="w-11 h-11 rounded-sm bg-zinc-100 flex items-center justify-center shrink-0">
        <Icon size={20} weight="bold" className="text-zinc-950" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{label}</div>
        <div className="font-bold">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} className="flex items-center gap-4 hover:text-[#FF4F00] transition-colors">{Inner}</a>
  ) : (
    <div className="flex items-center gap-4">{Inner}</div>
  );
};
